import moment from 'moment';
import { List, Set, Map, Range } from 'immutable';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';

const Hours_15 = 60 * 60 * 15;

export const calendarUrl = (isCloudPartner) => {
    if (isCloudPartner) {
        return 'https://www.joinflow.com/calendar/';
    } else {
        return 'https://support.telavox.com/hc/articles/115002667834-Calendar-integration-';
    }
};

/**
 * Check if the time stamps are considered 'all day'.
 *
 * @param start moment-js instance
 * @param end moment-js instance
 *
 * @return true when the two events are 24h apart, with time set to midnight
 */
export const isAllDay = (start, end) => {
    if (!moment.isMoment(start) || !moment.isMoment(end)) {
        console.warn('Illegal argument');

        return false;
    }

    const separatedBy24h = end.isSame(start.clone().add(1, 'days'));

    return startsAtMidnight(start) && separatedBy24h;
};

export const startsAtMidnight = (date) => {
    return date ? date.isSame(date.clone().startOf('day')) : false;
};

/**
 * Check if the calendar event is considered 'all day'.
 *
 * @param calendarEvent the calendar event DTO
 *
 * @return true when start and stop  events are 24h apart, with time set to midnight
 */
export const isAllDayEvent = (calendarEvent) => {
    if (calendarEvent.has('allDay')) {
        return calendarEvent.get('allDay');
    }

    const start = moment(calendarEvent.get('start'));
    const end = moment(calendarEvent.get('end'));

    return isAllDay(start, end);
};

/**
 * Expand recurring events into multiple, in the specified interval.
 *
 * @return List of events, with recurring events expanded into interval.
 */
export const inflateRecurringEvents = (events, intervalStart, intervalEnd) => {
    // Expand recurring events into a List in the specified interval
    const expandEvent = (event) => {
        const eventStart = moment(event.get('start'));

        if (
            event.get('occurence') === 'WEEKLY' &&
            eventStart.isBefore(intervalEnd)
        ) {
            const end = moment(event.get('end'));
            const duration = end.diff(eventStart, 'second');
            const interval = event.get('interval');

            let intervalWeeks = intervalEnd.diff(eventStart, 'week') + 1;
            const rruleUntil = getRruleUntil(event);
            if (rruleUntil) {
                intervalWeeks = Math.min(
                    intervalWeeks,
                    moment(rruleUntil).diff(eventStart, 'week') + 1,
                );
            }

            return Range(0, intervalWeeks, interval)
                .map((week) => {
                    // Generate one event per week in the amount of weeks that
                    // exist in the interval
                    return eventStart.clone().add(week, 'week');
                })
                .filter((time) => {
                    // Filter out generated events that ends before the
                    // interval start
                    return time
                        .clone()
                        .add(duration, 'second')
                        .isSameOrAfter(intervalStart);
                })
                .map((time) => {
                    // Clone the original event and update start and end
                    return event
                        .set('start', time.clone())
                        .set('end', time.clone().add(duration, 'second'));
                })
                .map((event) => {
                    // Convert moment to ISO8601-formatted strings
                    return event
                        .update('start', (start) =>
                            start.format(ISO_8601_MS_RESOLUTION),
                        )
                        .update('end', (end) =>
                            end.format(ISO_8601_MS_RESOLUTION),
                        );
                })
                .toList();
        }

        return List([event]);
    };
    // The backend leaves out events when the calendar has no content yet
    return (events ?? List()).flatMap(expandEvent);
};

// Convert ImmutableJS CalendarEventDTO-dates into Dates
const toDateEvent = (event) => {
    const toDate = (date) => date.toDate();

    return event.update('start', toDate).update('end', toDate);
};

/**
 * Create eventlist suitable for react-big-calendar out of OpeningHourDTOs, for the specified interval.
 */
export const inflateOpeningHours = (
    openingHours,
    intervalStart,
    intervalEnd,
) => {
    const startsAtTheSameTime = (date0, date1) => {
        return date0.isSame(date1);
    };

    // Check if interval specifies a 'free' day
    const isFreeAllDay = (start, end) => {
        return startsAtMidnight(start) && startsAtTheSameTime(start, end);
    };

    // Check if interval specifies a 'work all day' day
    const isWorkAllDay = (start, end) => {
        return (
            startsAtMidnight(start) &&
            startsAtMidnight(end) &&
            end.diff(start, 'day') === 1
        );
    };

    // Create a date relative to the interval start, with specified day offset.
    const createIntervalDate = (dayOffset) => {
        return intervalStart.clone().add(dayOffset, 'day');
    };

    // Create the CalendarEventDTOs for a given date, based on the
    // corresponding OpeningHourDTO.
    const intervalDateToCalendarEvents = (date) => {
        const openingHour = openingHours.get(getDayOfWeek(date));
        const ohStart = moment(openingHour.get('start'));
        const ohEnd = moment(openingHour.get('end'));

        const freeAllDay = isFreeAllDay(ohStart, ohEnd);
        const workAllDay = isWorkAllDay(ohStart, ohEnd);

        if (workAllDay) {
            return List();
        } else if (freeAllDay) {
            const event = Map({
                start: date.clone().startOf('day'),
                end: date.clone().endOf('day'),
                allDay: true,
                type: 'workHour',
            });

            return List([event]);
        } else {
            const eventMorning = Map({
                start: date.clone().startOf('day'),
                end: date
                    .clone()
                    .hour(ohStart.hour())
                    .minute(ohStart.minute())
                    .second(ohStart.second()),
                type: 'workHour',
            });

            if (
                ohEnd.hour() === 0 &&
                ohEnd.minute() === 0 &&
                ohEnd.second() === 0
            ) {
                return List([eventMorning]);
            }

            const eventEvening = Map({
                start: date
                    .clone()
                    .hour(ohEnd.hour())
                    .minute(ohEnd.minute())
                    .second(ohEnd.second()),
                end: date.clone().endOf('day'),
                type: 'workHour',
            });

            return List([eventMorning, eventEvening]);
        }
    };

    const intervalLength = intervalEnd.diff(intervalStart, 'day');

    return Range(0, intervalLength + 1)
        .map(createIntervalDate)
        .flatMap(intervalDateToCalendarEvents)
        .map(toDateEvent);
};

const spansMultipleDays = (event) => {
    const start = moment(event.get('start'));
    const end = moment(event.get('end'));

    return !start.isSame(end, 'day');
};

/**
 * Break one event into multiple if they are not marked as 'allDay', but they
 * span multiple days.
 *
 * @param CalendarEventDTO
 * @return a Immutable.List of events that are separated by day
 */
export const splitIntoMultipleDays = (event) => {
    const notAllDay = !isAllDayEvent(event);
    const multipleDays = spansMultipleDays(event);

    if (notAllDay && multipleDays) {
        const start = moment(event.get('start'));
        const end = moment(event.get('end'));

        if (end.diff(start, 'second') >= Hours_15) {
            const dayDiff = end
                .clone()
                .startOf('day')
                .diff(start.clone().startOf('day'), 'day');
            const dayOffsetToFulldayMomentEvent = (offset) => {
                return event
                    .set(
                        'start',
                        start.clone().add(offset, 'day').startOf('day'),
                    )
                    .set('end', start.clone().add(offset, 'day').endOf('day'))
                    .set('dayInterval', dayDiff + 1) //Not part of the regular eventDTO, used for GUI purpose only
                    .set('dayOffset', offset + 1); //Not part of the regular eventDTO, used for GUI purpose only
            };

            // Construct a list of events from the original midnight-spanning
            // event, resulting in a list where all events span the entire day,
            // except for the first and the last events, which have 'start' and
            // 'end' set to the original event.
            return Range(0, dayDiff + 1)
                .map(dayOffsetToFulldayMomentEvent)
                .toList()
                .update(0, (first) => first.set('start', start))
                .update(-1, (last) => last.set('end', end))
                .map(toDateEvent);
        }
    }

    return List([event]);
};

const getDayOfWeek = (date) => {
    // Look-up table for moment#isoWeekday
    const weekdays = [
        null,
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ];

    return weekdays[date.isoWeekday()];
};

const filteredWeekDays = (event) => {
    if (event) {
        const date = moment(event.get('start'));
        const dayOfWeek = getDayOfWeek(date);

        return !dayOfWeek.startsWith('s');
    }
    return null;
};

const isSameAsMonday = (event, monday) => {
    const start = moment(event.get('start'));
    const end = moment(event.get('end'));
    const mondayStart = moment(monday.get('start'));
    const mondayEnd = moment(monday.get('end'));

    const isSameTime =
        start.format('HH:mm') === mondayStart.format('HH:mm') &&
        end.format('HH:mm') === mondayEnd.format('HH:mm');
    const isOvernight =
        isAllDay(start, end) === isAllDay(mondayStart, mondayEnd);
    return isSameTime && isOvernight;
};

const copyDateTimeFromMonday = (event, monday, day) => {
    const dayOffset = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
    ].indexOf(day);
    return event
        .set(
            'start',
            moment(monday.get('start'))
                .add(dayOffset, 'days')
                .format(ISO_8601_MS_RESOLUTION),
        )
        .set(
            'end',
            moment(monday.get('end'))
                .add(dayOffset, 'days')
                .format(ISO_8601_MS_RESOLUTION),
        );
};

export const setSameAsMonday = (openingHours, monday) => {
    if (openingHours && !openingHours.isEmpty()) {
        return openingHours
            .filter((_event, day) =>
                [
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                ].includes(day),
            )
            .map((event, day) => copyDateTimeFromMonday(event, monday, day));
    }

    return null;
};

export const hasMatchingWeekdays = (openingHours, monday) => {
    if (openingHours && !openingHours.isEmpty()) {
        return (
            openingHours.filter(filteredWeekDays).filter((event) => {
                return isSameAsMonday(event, monday);
            }).size === 5
        );
    }
    return false;
};

export const canUseExternalCalendar = (allowedCalendarSources) => {
    const externalCalendarSources = Set.of('published', 'icsfile');
    return !externalCalendarSources.intersect(allowedCalendarSources).isEmpty();
};

// The backend sends source as an enum; compare case-insensitively so 'HOME'
// and 'home' both count as a calendar that is managed in Flow.
export const isExternallySyncedCalendar = (calendar) => {
    const source = calendar.get('source');
    return typeof source === 'string' && source.toLowerCase() !== 'home';
};

export const isFromSocialIntegration = (calendar) => {
    return calendar.get('url', '').indexOf('socialintegrationserver') > -1;
};

export const getCalendarWithActiveProfileAddedToEvents = (
    activeProfile,
    calendar,
) => {
    if (!activeProfile || activeProfile.isEmpty()) return calendar;

    const ongoingEvents = calendar
        .get('events')
        .filter((event) =>
            moment().isBetween(event.get('start'), event.get('end')),
        );

    const activeProfileAlreadyIncluded = ongoingEvents.some(
        (event) => event.getIn(['profile', 'key']) === activeProfile.get('key'),
    );

    let updatedCalendar = calendar;
    if (!activeProfileAlreadyIncluded && activeProfile.get('plusId') !== 0) {
        const startTime = moment(new Date())
            .format(ISO_8601_MS_RESOLUTION)
            .toString();
        const activeProfileEvent = new Map({
            profile: new Map({
                description: activeProfile.get('description'),
                key: activeProfile.get('key'),
            }),
            start: startTime,
            end: activeProfile.get('activeUntil'),
        });
        updatedCalendar = updatedCalendar.update('events', List(), (events) =>
            events.push(activeProfileEvent),
        );
    }
    return updatedCalendar;
};

export const getFilteredCalendarEvents = (
    events,
    startInterval,
    endInterval,
) => {
    return inflateRecurringEvents(events, startInterval, endInterval)
        .flatMap(splitIntoMultipleDays)
        .filter((event) =>
            moment(event.get('start')).isSameOrAfter(startInterval),
        )
        .filter((event) =>
            moment(event.get('start')).isSameOrBefore(endInterval),
        );
};

export const calculateBaseTime = (timestamp, selectedDay) => {
    const baseTime = moment(timestamp);
    // In some cases where time zones are involved, the event starts before midnight. This causes the baseTime
    // to be the wrong day. This code checks the day of the timestamp with the day you want to edit, shifting
    // the time to be correct in case they differ. See WEB-11253
    // E.g 2022-03-28T23:00:00.000+0200 which is a Monday, but is the opening hour start time for Tuesday
    const selectedDayIndex =
        selectedDay === 'monday-friday'
            ? 1
            : [
                  'sunday',
                  'monday',
                  'tuesday',
                  'wednesday',
                  'thursday',
                  'friday',
                  'saturday',
              ].indexOf(selectedDay);
    const baseTimeDayIndex = baseTime.day();
    const diff = selectedDayIndex - baseTimeDayIndex;
    return baseTime.add(diff >= 0 ? diff : diff + 7, 'days');
};

const getRruleObject = (event) => {
    if (!event.has('rrule')) {
        return {};
    }

    return Object.fromEntries(
        event
            .get('rrule')
            .split(';')
            .map((part) => part.split('=')),
    );
};

export const getRruleUntil = (event) => {
    const rrule = getRruleObject(event);

    return rrule?.UNTIL;
};
